import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { BedDouble, Bath, Car, TrendingUp, MapPin, ExternalLink, BadgeCheck, Home as HomeIcon, Images, X, ChevronLeft, ChevronRight, Layers, Calculator } from "lucide-react";

const ACCENT = "#00E676";

const photoSet = (num, count) =>
  Array.from({ length: count }, (_, i) => `/projects/buchanan/${num}-${String(i + 1).padStart(2, "0")}.jpg`);

// Real project photography — Buchanan scraped from public First National listings;
// Beresfield shot on site (plates blurred, cover cropped/cleaned).
const PROPERTIES = [
  {
    id: "buchanan-74",
    address: "74 Camargo Cct",
    loc: "Buchanan NSW 2323",
    sub: "Buchanan NSW 2323 · Master-Planned Residential Build",
    badge: "COMPLETED PROJECT — LEASED",
    tag: "Residential — $730/wk Lease Value",
    caption: "74 Camargo Cct, Buchanan — Completed $730/wk Dual-Asset Build",
    coverAlt: "74 Camargo Cct, Buchanan NSW — completed JAI HOMES residence facade",
    chips: [[BedDouble, "4", "Bed"], [Bath, "2", "Bath"], [Car, "2", "Auto Garage"]],
    features: [
      "Open-plan living + dedicated media room",
      "Hybrid flooring throughout",
      "Zoned ducted air conditioning",
      "Modern kitchen with stone benchtops",
    ],
    yieldTitle: "$700 – $730",
    yieldSuffix: "/ week",
    yieldNote: "Achieved weekly rental return — Hunter Expressway growth corridor",
    cta: { type: "listing", url: "https://www.firstnational.com.au/pages/real-estate/rent/property/3048278/74-camargo-circuit-buchanan-nsw-2323", label: "View 74 Camargo Cct Listing" },
    photos: photoSet("74", 16),
    fallback: "/projects/buchanan/74-02.jpg",
    thumbs: [
      { idx: 1, label: "Modern Kitchen — Stone Benchtops" },
      { idx: 3, label: "Open-Plan Living & Hybrid Flooring" },
      { idx: 4, label: "Media Room / Zoned AC Living" },
    ],
  },
  {
    id: "buchanan-76",
    address: "76 Camargo Cct",
    loc: "Buchanan NSW 2323",
    sub: "Buchanan NSW 2323 · Master-Planned Residential Build",
    badge: "COMPLETED PROJECT — LEASED",
    tag: "Residential — Dual-Occupancy Build",
    caption: "76 Camargo Cct, Buchanan — Completed $730/wk Dual-Asset Build",
    coverAlt: "76 Camargo Cct, Buchanan NSW — completed JAI HOMES residence facade",
    chips: [[BedDouble, "4", "Bed"], [Bath, "2", "Bath"], [Car, "2", "Auto Garage"]],
    features: [
      "Open-plan living + dedicated media room",
      "Hybrid flooring throughout",
      "Zoned ducted air conditioning",
      "Modern kitchen with stone benchtops",
    ],
    yieldTitle: "$700 – $730",
    yieldSuffix: "/ week",
    yieldNote: "Achieved weekly rental return — Hunter Expressway growth corridor",
    cta: { type: "listing", url: "https://www.lakemacquariefn.com.au/pages/real-estate/rent/property/3048290/76-camargo-circuit-buchanan-nsw-2323", label: "View 76 Camargo Cct Listing" },
    photos: photoSet("76", 17),
    fallback: "/projects/buchanan/76-02.jpg",
    thumbs: [
      { idx: 3, label: "Zoned AC Living Area" },
      { idx: 1, label: "Modern Kitchen — Stone Benchtops" },
      { idx: 7, label: "Master Suite — Walk-In Robe" },
    ],
  },
  {
    id: "beresfield-duplex",
    address: "Modern Double-Storey Duplex",
    loc: "Beresfield NSW 2322",
    sub: "Unit 1 & 2 · 23A Fletcher St, Beresfield NSW 2322",
    badge: "COMPLETED PROJECT — DUAL-OCCUPANCY BUILD",
    tag: "High-Yield Dual-Occupancy / Multi-Dwelling Build",
    caption: "23A Fletcher St, Beresfield — Completed Dual-Occupancy Duplex",
    coverAlt: "Modern double-storey duplex at 23A Fletcher St, Beresfield NSW — completed JAI HOMES dual-occupancy build",
    chips: [[HomeIcon, "2", "Units"], [Layers, "2", "Storeys"], [Car, "2", "Auto Garages"]],
    features: [
      "Double-storey contemporary design with custom weatherboard facade",
      "Glass balustrade balconies & timber-finish automatic garaging",
      "Independent dual-living layouts engineered for maximum rental yield",
      "Full turn-key handover including landscaping & concreting",
    ],
    yieldTitle: "2 Income Streams",
    yieldSuffix: "· dual-key",
    yieldNote: "Independent dual-living layouts — engineered for maximum rental yield",
    cta: { type: "calc", label: "Calculate Duplex Yield Potential" },
    photos: [
      "/projects/duplex-beresfield/dx-01.jpg",
      "/projects/duplex-beresfield/dx-02.jpg",
      "/projects/duplex-beresfield/dx-03.jpg",
      "/projects/duplex-beresfield/dx-04.jpg",
    ],
    fallback: "/projects/duplex-beresfield/dx-01.jpg",
    thumbs: [
      { idx: 1, label: "Unit 2 — Balcony & Glass Balustrade" },
      { idx: 2, label: "Twin Gable Frontage — Both Units" },
      { idx: 3, label: "Unit 1 — Weatherboard Elevation" },
    ],
  },
];

const PropertyImg = ({ src, fallback, alt, className, onClick, testId }) => {
  const [current, setCurrent] = useState(src);
  useEffect(() => setCurrent(src), [src]);
  return (
    <img
      src={current}
      alt={alt}
      loading="lazy"
      data-testid={testId}
      onClick={onClick}
      onError={() => current !== fallback && setCurrent(fallback)}
      className={className}
    />
  );
};

const Lightbox = ({ property, index, onClose, onNav }) => {
  const total = property.photos.length;
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [index, property]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[96] bg-[#0A0E17]/95 backdrop-blur-md flex flex-col"
      onClick={onClose}
      data-testid="lightbox"
    >
      <div className="flex items-center justify-between px-5 sm:px-8 py-4 shrink-0">
        <div>
          <p className="font-display text-sm font-bold text-white">{property.address}, {property.loc}</p>
          <p className="text-[11px] text-white/50" data-testid="lightbox-counter">
            Photo {index + 1} of {total}
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          aria-label="Close gallery"
          className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-[#00E676]/60 transition-colors duration-300"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center px-4 sm:px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={`${property.id}-${index}-${failed}`}
            src={failed ? property.fallback : property.photos[index]}
            alt={`${property.address} — photo ${index + 1}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            onError={() => setFailed(true)}
            className="max-h-full max-w-full object-contain rounded-xl shadow-[0_30px_100px_rgba(0,0,0,0.7)]"
            data-testid="lightbox-img"
          />
        </AnimatePresence>

        <button
          onClick={() => onNav(-1)}
          data-testid="lightbox-prev"
          aria-label="Previous photo"
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:border-[#00E676]/60 hover:scale-110 transition-all duration-300"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => onNav(1)}
          data-testid="lightbox-next"
          aria-label="Next photo"
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:border-[#00E676]/60 hover:scale-110 transition-all duration-300"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="shrink-0 px-5 sm:px-8 pb-5 pt-3 overflow-x-auto">
        <div className="flex gap-2 justify-center">
          {property.photos.map((p, i) => (
            <button
              key={p}
              onClick={(e) => {
                e.stopPropagation();
                onNav(i - index);
              }}
              data-testid={`lightbox-thumb-${i}`}
              aria-label={`Go to photo ${i + 1}`}
              className={`shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition-all duration-300 ${
                i === index ? "border-[#00E676] shadow-[0_0_12px_rgba(0,230,118,0.5)]" : "border-white/15 opacity-50 hover:opacity-90"
              }`}
            >
              <img src={p} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </motion.div>,
    document.body
  );
};

export const CaseStudy = () => {
  const [lightbox, setLightbox] = useState(null); // { propIdx, photoIdx }
  const lenis = useLenis();

  const goCalc = () => {
    window.dispatchEvent(new CustomEvent("jai:preset-asset", { detail: "villa" }));
    if (lenis) lenis.scrollTo("#calculator", { offset: -110, duration: 1.4 });
    else document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" });
  };

  const nav = useCallback(
    (delta) =>
      setLightbox((lb) => {
        if (!lb) return lb;
        const total = PROPERTIES[lb.propIdx].photos.length;
        return { ...lb, photoIdx: (lb.photoIdx + delta + total) % total };
      }),
    []
  );

  return (
    <section id="case-study" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 bg-[#111827]" data-testid="case-study-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#00E676] mb-4 flex items-center gap-3">
            <BadgeCheck size={14} /> DELIVERED CASE STUDIES — COMPLETED NSW PROJECTS
          </p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
            Proof, not promises.{" "}
            <em className="font-serif-lux italic font-semibold" style={{ color: ACCENT }}>Real NSW builds.</em>
          </h2>
          <p className="mt-5 max-w-2xl text-sm sm:text-base text-white/60 leading-relaxed flex items-start gap-2">
            <MapPin size={16} className="shrink-0 mt-0.5" style={{ color: ACCENT }} />
            <span>
              From the Buchanan Ridge residential pair at 74 &amp; 76 Camargo Cct to the dual-occupancy
              duplex at 23A Fletcher St, Beresfield — real addresses, real photography, real outcomes
              in high-demand Hunter growth corridors.
            </span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PROPERTIES.map((p, propIdx) => (
            <motion.article
              key={p.id}
              data-testid={`case-card-${p.id}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: propIdx * 0.12 }}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#00E676]/40 transition-colors duration-300"
            >
              <div className="relative h-64 sm:h-72 overflow-hidden cursor-pointer" onClick={() => setLightbox({ propIdx, photoIdx: 0 })}>
                <PropertyImg
                  src={p.photos[0]}
                  fallback={p.fallback}
                  alt={p.coverAlt}
                  testId={`case-featured-${p.id}`}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-108"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-transparent pointer-events-none" />
                <span
                  className="absolute top-5 left-5 rounded-full px-4 py-2 font-display text-[10px] font-bold tracking-[0.18em] text-[#0A0E17] bg-[#00E676] shadow-[0_0_20px_rgba(0,230,118,0.5)]"
                  data-testid={`case-badge-${p.id}`}
                >
                  {p.badge}
                </span>
                <span className="absolute bottom-4 left-4 max-w-[62%] rounded-full glass border border-white/15 px-3.5 py-1.5 font-display text-[10px] font-bold tracking-wide text-white/85 pointer-events-none" data-testid={`case-caption-${p.id}`}>
                  {p.caption}
                </span>
                <span className="absolute bottom-4 right-4 rounded-full glass border border-white/20 px-3.5 py-1.5 font-display text-[11px] font-bold text-white inline-flex items-center gap-1.5" data-testid={`case-photocount-${p.id}`}>
                  <Images size={13} style={{ color: ACCENT }} /> {p.photos.length} Photos
                </span>
              </div>

              <div className="p-7 sm:p-8 border-t-2" style={{ borderTopColor: ACCENT }}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold text-2xl text-white tracking-tight">{p.address}</h3>
                    <p className="text-xs text-white/50 mt-1">{p.sub}</p>
                    <span
                      className="mt-2.5 inline-block rounded-full px-3 py-1 font-display text-[10px] font-bold tracking-[0.14em] border"
                      style={{ color: ACCENT, borderColor: `${ACCENT}55`, background: `${ACCENT}12` }}
                      data-testid={`case-tag-${p.id}`}
                    >
                      {p.tag.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex gap-4">
                    {p.chips.map(([Icon, val, label]) => (
                      <div key={label} className="text-center">
                        <Icon size={17} style={{ color: ACCENT }} className="mx-auto" />
                        <p className="font-display text-sm font-bold text-white mt-1">{val}</p>
                        <p className="text-[9px] text-white/45 tracking-wide uppercase">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-2" data-testid={`case-thumbs-${p.id}`}>
                  {p.thumbs.map((t) => (
                    <button
                      key={t.idx}
                      onClick={() => setLightbox({ propIdx, photoIdx: t.idx })}
                      data-testid={`case-thumb-${p.id}-${t.idx}`}
                      className="group/thumb text-left"
                    >
                      <span className="block h-28 sm:h-20 rounded-lg overflow-hidden border border-white/10 group-hover/thumb:border-[#00E676]/50 transition-colors duration-300">
                        <PropertyImg
                          src={p.photos[t.idx]}
                          fallback={p.fallback}
                          alt={t.label}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover/thumb:scale-110"
                        />
                      </span>
                      <span className="mt-1.5 block text-[9px] text-white/45 leading-tight">{t.label}</span>
                    </button>
                  ))}
                </div>

                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-white/65 leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ACCENT, boxShadow: `0 0 8px ${ACCENT}` }} />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 rounded-xl border border-[#00E676]/25 bg-[#00E676]/[0.06] px-5 py-4 flex items-center gap-4" data-testid={`case-yield-${p.id}`}>
                  <TrendingUp size={20} style={{ color: ACCENT }} className="shrink-0" />
                  <div>
                    <p className="font-display text-lg font-extrabold text-white tracking-tight">
                      {p.yieldTitle} <span className="text-sm font-semibold text-white/50">{p.yieldSuffix}</span>
                    </p>
                    <p className="text-[11px] text-white/50">{p.yieldNote}</p>
                  </div>
                </div>

                {p.cta.type === "listing" ? (
                  <a
                    href={p.cta.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`case-listing-${p.id}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold text-[#0A0E17] bg-[#00E676] hover:shadow-[0_0_26px_rgba(0,230,118,0.5)] hover:scale-[1.03] transition-all duration-300"
                  >
                    {p.cta.label} <ExternalLink size={15} />
                  </a>
                ) : (
                  <button
                    onClick={goCalc}
                    data-testid={`case-calc-${p.id}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold text-[#0A0E17] bg-[#00E676] hover:shadow-[0_0_26px_rgba(0,230,118,0.5)] hover:scale-[1.03] transition-all duration-300"
                  >
                    {p.cta.label} <Calculator size={15} />
                  </button>
                )}
              </div>
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 text-center text-xs text-white/40 flex items-center justify-center gap-2"
        >
          <HomeIcon size={14} style={{ color: ACCENT }} />
          Independently listed &amp; leased — verify live performance via the public listings above.
        </motion.p>
      </div>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            property={PROPERTIES[lightbox.propIdx]}
            index={lightbox.photoIdx}
            onClose={() => setLightbox(null)}
            onNav={nav}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
